export interface FrameAnalysisRequest {
  frameNumber: number;
  timestamp: number;
  speakerLabel: string;
  occurrenceNumber: number;
}

export interface FrameAnalysisResult {
  speakerLabel: string;
  frameNumber: number;
  timestamp: number;
  frameUrl: string;
  hasLowerThird: boolean;
  name?: string;
  title?: string;
  company?: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface TranscriptSegment {
  speaker: string;
  start: number;
  end: number;
  text: string;
}

/**
 * Get frame numbers for speaker analysis
 * Extracts frames 3 seconds after each speaker starts talking (up to 3 times per speaker)
 */
export function getFrameNumbersForSpeakerAnalysis(
  segments: TranscriptSegment[],
): FrameAnalysisRequest[] {
  const speakerFrames: FrameAnalysisRequest[] = [];
  const speakerOccurrences: Record<string, number> = {};

  segments.forEach((segment, index) => {
    const speaker = segment.speaker;

    // Initialize counter for new speakers
    if (!speakerOccurrences[speaker]) {
      speakerOccurrences[speaker] = 0;
    }

    // Check if this is a new occurrence of this speaker
    const isNewSpeakerOccurrence =
      index === 0 || segment.speaker !== segments[index - 1].speaker;

    // Only process if it's a new occurrence and we haven't hit the limit
    if (isNewSpeakerOccurrence && speakerOccurrences[speaker] < 3) {
      // Get frame 3 seconds after speaker starts
      // Since thumbnails are generated every second, we round to the nearest second
      const frameNumber = Math.floor(segment.start + 3);

      // Only add if the frame is within the segment duration
      if (frameNumber < segment.end) {
        speakerFrames.push({
          frameNumber,
          timestamp: segment.start + 3,
          speakerLabel: speaker,
          occurrenceNumber: speakerOccurrences[speaker] + 1,
        });

        speakerOccurrences[speaker]++;
      }
    }
  });

  return speakerFrames;
}

/**
 * Format timestamp for display (e.g., 1:23)
 */
export function formatTimestamp(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get frame numbers for dense analysis (every N seconds for first occurrence)
 * Used for Pass 3 when other methods haven't identified the speaker
 */
export function getDenseFrameNumbersForSpeaker(
  segments: TranscriptSegment[],
  speakerLabel: string,
  intervalSeconds: number = 5,
  maxFrames: number = 10,
): FrameAnalysisRequest[] {
  const speakerFrames: FrameAnalysisRequest[] = [];

  // Find the first segment for this speaker
  const firstSegment = segments.find(seg => seg.speaker === speakerLabel);
  if (!firstSegment) return [];

  // Starting from 3 seconds after they start speaking
  let currentTime = firstSegment.start + 3;
  const endTime = Math.min(
    firstSegment.end,
    firstSegment.start + intervalSeconds * maxFrames,
  );

  let frameCount = 0;
  while (currentTime < endTime && frameCount < maxFrames) {
    speakerFrames.push({
      frameNumber: Math.floor(currentTime),
      timestamp: currentTime,
      speakerLabel: speakerLabel,
      occurrenceNumber: frameCount + 1,
    });

    currentTime += intervalSeconds;
    frameCount++;
  }

  return speakerFrames;
}
